import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { SubscripionPromotion } from '@env/types';
import firebase from 'firebase/app';
import { SimpleModalComponent, SimpleModalService } from '@looorent/ngx-simple-modal';
import { Subscription } from 'rxjs';

import { environment } from '@env/environment';

import {
    AdSupportedCohortConfig,
    IStoreService,
    SubscriptionProduct,
    SubscriptionState,
    SubscriptionTier,
} from '@app/shared/services/store/store-service.interface';
import { WaitModalService } from '@app/shared/services/wait-modal.service';
import { waitPromise } from '@app/shared/utilities';
import { AnalyticsEvent, SingularEvent, LogAnalyticsEventInteractor, UserModel, UserService } from '@together/common';
import { AlertModalComponent } from '../alert-modal/alert-modal.component';
import { WebviewModalComponent } from '../webview-modal/webview-modal.component';

export interface UpgradeAdsBasedModal {
    preSelectTier?: string;
}

@Component({
    selector: 'app-upgrade-ads-based-modal',
    templateUrl: './upgrade-ads-based-modal.component.html',
    styleUrls: ['./upgrade-ads-based-modal.component.scss'],
})
export class UpgradeAdsBasedModalComponent
    extends SimpleModalComponent<UpgradeAdsBasedModal, void>
    implements OnInit, OnDestroy
{
    public disablePurchase: boolean = false;
    public selectedProductToPurchase: SubscriptionProduct;
    public selectedProduct: SubscriptionProduct;
    public tier: SubscriptionTier;
    public isSubscribed: boolean;

    protected user: UserModel;
    protected subs = new Subscription();
    public adSupportedCohortConfig: AdSupportedCohortConfig;

    constructor(
        protected logAnalyticsEvent: LogAnalyticsEventInteractor,
        @Inject('FirebaseFunctions') protected functions: firebase.functions.Functions,
        protected modalService: SimpleModalService,
        @Inject('FirebaseRemoteConfig') protected remoteConfig: firebase.remoteConfig.RemoteConfig,
        @Inject('StoreService') protected storeService: IStoreService,
        protected userService: UserService,
        protected waitService: WaitModalService,
    ) {
        super();
    }

    public async ngOnInit(): Promise<void> {
        [this.user, this.adSupportedCohortConfig] = await Promise.all([
            this.userService.getUser(),
            this.storeService.getAdSupportedCohortConfig(),
        ]);

        this.tier = this.adSupportedCohortConfig?.subscriptionTier;

        this.logAnalyticsEvent.execute(AnalyticsEvent.SubscriptionScreenDisplayed, {
            subscription_status: this.user.analyticsType,
        });

        this.logAnalyticsEvent.execute(SingularEvent.SngCheckoutInitiated);

        this.selectBestValueProduct();

        // Get subscription state & watch for changes
        this.isSubscribed = this.user.isSubscribed();

        this.subs.add(
            this.storeService.getSubscriptionState$().subscribe(state => {
                this.isSubscribed = state.isSubscribed;
            }),
        );
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    public async purchase(product, inApp: boolean = true): Promise<void> {
        this.selectedProductToPurchase = product;
        let result: SubscriptionState;

        this.waitService.show();
        this.logAnalyticsEvent.execute(AnalyticsEvent.SubscriptionClickBuy, {
            product_id: this.selectedProductToPurchase.id,
            subscription_status: this.user.analyticsType,
        });

        try {
            result = await this.storeService.purchaseSubscription(this.selectedProductToPurchase, inApp);
            if (result.isSubscribed) {
                this.close();
                this.storeService.showBuySubscriptionSuccessModal();
                this.logAnalyticsEvent.execute(AnalyticsEvent.SubscriptionPurchased, {
                    product_id: this.selectedProductToPurchase.id,
                    subscription_status: this.user.analyticsType,
                });
                this.logAnalyticsEvent.execute(SingularEvent.SngSubscribe);
            }
        } finally {
            this.waitService.hide();
        }
    }

    public selectProduct(product: SubscriptionProduct): void {
        this.selectedProduct = product;
    }

    public selectBestValueProduct(): void {
        const bestValueProduct = this.tier?.products?.find(p => p.isBestValue);
        this.selectedProduct = bestValueProduct || this.tier?.products?.[0];
    }

    public getCTA(): string {
        return this.adSupportedCohortConfig?.buttonCTA || 'Unlock an ad-free experience';
    }

    public getSelectedPlanFooterText(): string {
        if (!this.selectedProduct) {
            return '';
        }
        return `${this.selectedProduct.price}/${this.selectedProduct.period} · Cancel anytime`;
    }

    public getPerMonthPrice(product: SubscriptionProduct): string {
        return product?.pricePerMonth || product?.price || '';
    }

    public getOrderedProducts(): SubscriptionProduct[] {
        return [...(this.tier?.products || [])].sort((a, b) => Number(!!b?.isBestValue) - Number(!!a?.isBestValue));
    }

    public async restorePurchases(): Promise<void> {
        try {
            this.waitService.show();
            await waitPromise(this.storeService.restorePurchases(), 4000);
        } catch (err) {
            this.modalService.addModal(AlertModalComponent, {
                title: 'Oops!',
                message: `There was an issue restoring your purchases. Please try again.`,
                type: 'error',
                icon: 'error',
            });
        } finally {
            this.waitService.hide();
        }
    }
}
